//utils that include react at some point. Can't be used in scripts

import React from 'react';
import ReactDOM from 'react-dom';
import { Point, getDist } from './components/coreui';

import { fromBack, indexOfMax, normalizeAngle, nonNull } from './utils';

export * from './utils';


export const getAngle = (a: Point, b: Point) => Math.atan2(b.y - a.y, b.x - a.x);


export const rotate = (vec: Point, angle: number) => ({
  x: Math.cos(angle) * vec.x - Math.sin(angle) * vec.y,
  y: Math.sin(angle) * vec.x + Math.cos(angle) * vec.y
});

export const sufficientlyDifferent = (a: Point, b: Point, overlayScale: number) => {
  const dist = getDist(a, b);
  const different = dist / overlayScale > 5;
  return { different, dist };
};

export const onSegment = (a: Point, b: Point, test: Point) => {
  const atb = getDist(a, test) + getDist(test, b);
  const ab = getDist(a, b);
  return atb < 1.001 * ab;
};


export const segementIntersect = (f: { a: Point, b: Point }, g: { a: Point, b: Point }) => {
  // see https://en.wikipedia.org/wiki/Line%E2%80%93line_intersection
  const denom = (f.a.x - f.b.x) * (g.a.y - g.b.y) - (f.a.y - f.b.y) * (g.a.x - g.b.x); //(x1 - x2)(y3 - y4) - (y1 - y2)(x3 - x4)
  if (Math.abs(denom) < 1e-6) //parallel
    return null;
  
  const [lower, upper] = denom < 0 ? [denom, 0] : [0, denom];
  
  const tNum = (f.a.x - g.a.x) * (g.a.y - g.b.y) - (f.a.y - g.a.y) * (g.a.x - g.b.x); //(x1 - x3)(y3 - y4) - (y1 - y3)(x3 - x4)
  if (tNum <= lower || tNum >= upper) // not on segment f
    return null;
  
  const uNum = -((f.a.x - f.b.x) * (f.a.y - g.a.y) - (f.a.y - f.b.y) * (f.a.x - g.a.x)); //(x1 - x2)(y1 - y3) - (y1 - y2)(x1 - x3)
  if (uNum <= lower || uNum >= upper) // not on segment g
    return null;
  
  const t = tNum / denom;
  return { x: f.a.x + t * (f.b.x - f.a.x), y: f.a.y + t * (f.b.y - f.a.y) } as Point;
};

export const getNonIntersectingArea = (path: Point[]) => {
  if (path.length < 3)
    return 0;
  let area = 0;
  for (var i = 0; i < path.length; ++i) {
    const p1 = path[i];;
    const p2 = path[(i + path.length - 1) % path.length];
    area += (p2.x + p1.x) * (p2.y - p1.y);
  }
  return Math.abs(area / 2);
};

export const splitIntoNonIntersecting = (path: Point[]) => {
  if (path.length === 0)
    return [path];
  
  if (path.length <= 2)
    return [path];
  
  const split = (path: Point[]): Point[][] => {
    // inefficient, but will do
    for (let i = 0; i < path.length - 2; ++i) {
      for (let j = i + 2; j < path.length; ++j) {
        const intersect = segementIntersect({ a: path[i], b: path[i + 1] }, { a: path[j], b: path[(j + 1) % path.length] });
        if (intersect) {
          // split into two subpaths
          const path1 = [...path.slice(0, i + 1), intersect, ...path.slice(j + 1)];
          const path2 = [intersect, ...path.slice(i + 1, j + 1)];
          return [...split(path1), ...split(path2)];
        }
      }
    }
    // no intersection: return path without the last item
    return [path];
  }

  return split(path);
};

export const getLengthTextTransforms = (path: Point[]) => {
  const distances = path.slice(1).map((point, index) => getDist(point, path[index]));
  const all = distances.map((distance, index) => {

    const [a, b] = path.slice(index, index + 2);

    const { angle: segmentAngle } = normalizeAngle(getAngle(a, b));

    const center = {
      x: (b.x + a.x) / 2,
      y: (b.y + a.y) / 2
    }

    return { length: distance, position: center, angle: segmentAngle };
  });
  return { length: all.reduce((sum, elem) => sum + elem.length, 0), renderText: all };
};

export const getAreaTextTransforms = (path: Point[]) => {
  const nonIntersecting = splitIntoNonIntersecting(path);
  const all =  nonIntersecting.map((path, index) => {
    const area = getNonIntersectingArea(path);

    const [maxX, centerY] = path.reduce(([maxX, centerY], { x, y }) => [Math.max(maxX, x), centerY + y / path.length], [0, 0]);
    const horizontalLine = { a: { x: 0, y: centerY }, b: { x: maxX + 100, y: centerY } };
    let intersections = path.map((p, i) => segementIntersect(horizontalLine, { a: p, b: path[(i + 1) % path.length] })).filter(nonNull);
    if (intersections.length < 2) {
      return { area, position: { x: path.reduce((c, p) => c + p.x / path.length, 0), y: centerY }, space: 0 };
    }
    intersections.sort((a, b) => a.x - b.x);
    const xIntervals = [...Array(Math.floor(intersections.length / 2)).keys()].map(i => [intersections[2*i].x, intersections[2*i + 1].x]);
    
    const xLengths = xIntervals.map(([x1, x2]) => Math.abs(x2 - x1));
    const maxXLenIndex = indexOfMax(xLengths);
  
    const centerX = (xIntervals[maxXLenIndex][0] + xIntervals[maxXLenIndex][1]) / 2;

    return { area, position: { x: centerX, y: centerY }, space: xLengths[maxXLenIndex] };
  });
  return { area: all.reduce((sum, elem) => sum + elem.area, 0), areaText: all };
};

export const getIndexTransform = (path: Point[]) => {
  const startAngle = normalizeAngle(getAngle(path[0], path[1]));
  const endAngle = normalizeAngle(getAngle(fromBack(path), fromBack(path, 1)));
  const useStart = (Math.abs(startAngle.angle - endAngle.angle) < 1) ? endAngle.flipped : Math.abs(startAngle.angle) < Math.abs(endAngle.angle);
  return useStart ? { iAngle: startAngle, iAnchor: path[0] } : { iAngle: endAngle, iAnchor: fromBack(path) };
  
};

export const getFittingString = (val: number, space: number, postFix?: string) => {
  const decim = val.toFixed(2);
  const noDecim = Math.round(val).toString();
  let textOpts = [
    `${decim}`,
    `${noDecim}`
  ];
  if (postFix) {
    textOpts.splice(0, 0, ...textOpts.map(str => str + postFix));
  }
  
  const textOverflow = textOpts.map(text => text.length * 16 - space);
  // rough estimage: char width = 16
  const textIndex = indexOfMax([...textOverflow.slice(0, -1).map(of => of < 0 ? 1 : 0), 1]);
  
  return { overflow: textOverflow[textIndex], text: textOpts[textIndex] };
};

export const insertIntoPath = (oldPath: Point[], newPoint: Point) => {
  let path = [...oldPath];
  let bestSegmentIndex = 0;
  let shortestSegment = Number.MAX_VALUE;

  // insert in segment that adds the least overall length
  for (let i = 1; i < path.length; i++) {
    const test = (getDist(path[i - 1], newPoint) + getDist(path[i], newPoint)) / getDist(path[i - 1], path[i]);
    if (test < shortestSegment) {
      shortestSegment = test;
      bestSegmentIndex = i;
    }
  }

  path.splice(bestSegmentIndex, 0, newPoint);
  return { index: bestSegmentIndex, path };
};


// expects closed path, i.e. path[0] === path[path.length - 1]
export const getArea = (path: Point[]): number => 
  splitIntoNonIntersecting(path).reduce((sum, p) => sum + getNonIntersectingArea(p), 0);

export const getPathLength = (path: Point[]) => {
  return path.slice(1).reduce((acc, point, index) => acc + getDist(point, path[index]), 0);
};


export const JSXToNode = (node: React.ReactElement) => {
  return new Promise<any>((resolve, reject) => {
    const div = document.createElement('div');
    ReactDOM.render(node, div, () => {
      if (div.firstChild) {
        resolve(div.firstChild);
      } else {
        reject("Couldn't render element");
      }
    });
  });
};